import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Home, ArrowUpCircle, ArrowLeftRight, Send, CreditCard, Users, History,
  Shield, Settings, QrCode, Link2, Code, Grid3X3, Award, HelpCircle,
  GraduationCap, LogOut, Search, Download, ChevronDown, Calendar, Check, Bell, Copy
} from "lucide-react";

const generateRandomId = () => Math.floor(100000 + Math.random() * 900000);

export default function CashinoutPaymentsPreview() {
  const [searchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [browserTheme, setBrowserTheme] = useState('dark');
  const [randomUserId] = useState(generateRandomId);

  useEffect(() => {
    document.title = 'История Платежей';
    const tp = searchParams.get('theme');
    if (tp === 'dark' || tp === 'light') setBrowserTheme(tp);
    let fav = document.querySelector("link[rel='icon']");
    if (!fav) { fav = document.createElement('link'); fav.rel = 'icon'; document.head.appendChild(fav); }
    fav.href = 'https://cashinout.io/favicon.ico';
  }, []);

  useEffect(() => {
    const dp = searchParams.get('data');
    if (dp) {
      try {
        const parsed = JSON.parse(decodeURIComponent(dp));
        setData(parsed);
        if (parsed.browserTheme) setBrowserTheme(parsed.browserTheme);
        const pp = searchParams.get('page');
        if (pp) setCurrentPage(parseInt(pp) || 1);
        if (parsed.transactions?.length > 0) {
          const dates = parsed.transactions.map(tx => {
            if (!tx.date) return null;
            const parts = tx.date.split('.');
            return parts.length === 3 ? { sortKey: new Date(parts[2], parts[1]-1, parts[0]).getTime(), d: tx.date } : null;
          }).filter(Boolean).sort((a,b)=>a.sortKey-b.sortKey);
          if (dates.length > 0) { setDateFrom(dates[0].d); setDateTo(dates[dates.length-1].d); }
        }
      } catch (e) { console.error(e); }
    }
  }, [searchParams]);

  const isDark = browserTheme === 'dark';
  const theme = {
    bg: isDark ? '#202124' : '#dee1e6', tabBarBg: isDark ? '#202124' : '#dee1e6',
    activeTabBg: isDark ? '#292a2d' : '#ffffff', inactiveTabBg: isDark ? '#35363a' : '#c9cdd4',
    addressBarBg: isDark ? '#292a2d' : '#ffffff', addressInputBg: isDark ? '#202124' : '#f1f3f4',
    textPrimary: isDark ? '#e8eaed' : '#202124', textSecondary: isDark ? '#9aa0a6' : '#5f6368',
    border: isDark ? '#3c4043' : '#dadce0',
  };

  useEffect(() => {
    document.documentElement.style.backgroundColor = theme.bg;
    document.body.style.backgroundColor = theme.bg;
    document.body.style.margin = "0"; document.body.style.padding = "0";
    return () => { document.documentElement.style.backgroundColor = ""; document.body.style.backgroundColor = ""; };
  }, [theme.bg]);

  if (!data) return <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center text-gray-600">Загрузка...</div>;

  const allTransactions = data.transactions || [];
  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const userId = data.cashinoutId || randomUserId;

  // Filter ONLY outgoing (withdrawal) transactions
  let filteredTransactions = allTransactions.filter(tx => tx.type === 'withdrawal');
  filteredTransactions.sort((a, b) => {
    const pdt = (d, t) => { if (!d) return 0; const p = d.split('.'); return p.length === 3 ? new Date(`${p[2]}-${p[1]}-${p[0]}T${t||'00:00'}`).getTime() : 0; };
    return pdt(a.date, a.time) - pdt(b.date, b.time);
  });

  // Compute date range from first to last
  const computedDateFrom = filteredTransactions.length > 0 ? filteredTransactions[0].date : dateFrom;
  const computedDateTo = filteredTransactions.length > 0 ? filteredTransactions[filteredTransactions.length - 1].date : dateTo;

  const itemsPerPage = 15;
  const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / itemsPerPage));
  const transactions = filteredTransactions.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

  const BrowserFrame = () => (
    <div style={{ backgroundColor: theme.tabBarBg }} className="flex-shrink-0">
      <div className="flex items-center h-[38px]" style={{ backgroundColor: theme.tabBarBg }}>
        <div className="flex items-end flex-1 pl-2">
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px]" style={{ backgroundColor: theme.inactiveTabBg }}>
            <img src="https://finance.ozon.ru/favicon.ico" alt="" className="w-4 h-4 mr-2" />
            <span className="text-[13px] truncate" style={{ color: theme.textSecondary }}>Ozon Карта</span>
          </div>
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px]" style={{ backgroundColor: theme.activeTabBg }}>
            <img src="https://cashinout.io/favicon.ico" alt="" className="w-4 h-4 mr-2" />
            <span className="text-[13px] truncate" style={{ color: theme.textPrimary }}>История Платежей</span>
          </div>
          <button className="w-7 h-7 flex items-center justify-center rounded-full ml-1 mb-1" style={{ color: theme.textSecondary }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          </button>
        </div>
        <div className="flex items-center h-full">
          <button className="w-[46px] h-full flex items-center justify-center" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[1px]" viewBox="0 0 10 1"><rect fill="currentColor" width="10" height="1"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1"><rect x="0.5" y="0.5" width="9" height="9"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#e81123] hover:text-white" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.2"><line x1="0" y1="0" x2="10" y2="10"/><line x1="10" y1="0" x2="0" y2="10"/></svg></button>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-2" style={{ backgroundColor: theme.addressBarBg }}>
        <div className="flex items-center gap-1 mr-2">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: isDark ? '#5f6368' : '#dadce0' }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg></button>
        </div>
        <div className="flex-1 flex items-center h-[32px] px-4 rounded-full" style={{ backgroundColor: theme.addressInputBg, border: `1px solid ${theme.border}` }}>
          <svg className="w-4 h-4 mr-3" style={{ color: theme.textSecondary }} viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
          <span className="text-[14px]" style={{ color: theme.textPrimary }}>cashinout.io/history</span>
        </div>
      </div>
    </div>
  );

  const menuItems = [
    { icon: Home, label: 'Главная' }, { icon: ArrowUpCircle, label: 'Пополнить' },
    { icon: ArrowLeftRight, label: 'Обмен' }, { icon: Send, label: 'Отправить' },
    { icon: CreditCard, label: 'Виртуальные карты' }, { icon: Users, label: 'Реферальная программа' },
    { icon: History, label: 'История', active: true }, { icon: Shield, label: '2FA' },
    { icon: Settings, label: 'Настройки' },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: theme.bg, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <BrowserFrame />
      <div className="flex-1 flex bg-[#f5f7fa]">
        <aside className="w-56 bg-[#303646] flex flex-col flex-shrink-0">
          <div className="p-4"><img src="/cashinout-logo.png" alt="Cashinout" className="h-8 object-contain" /></div>
          <nav className="flex-1 py-2 overflow-y-auto">
            {menuItems.map((item, i) => {
              const Icon = item.icon;
              return (<a key={i} href="#" className={`flex items-center gap-3 px-4 py-2.5 text-sm ${item.active ? 'text-white bg-[#3d4557]' : 'text-[#9ca3af]'}`}><Icon className="w-5 h-5" /><span>{item.label}</span></a>);
            })}
            <div className="px-4 py-2 mt-4"><span className="text-xs font-medium text-[#6b7280] uppercase">Приложение</span></div>
            <a href="#" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#9ca3af]"><QrCode className="w-5 h-5" /><span>QR для авторизации</span></a>
            <div className="px-4 py-2 mt-4"><span className="text-xs font-medium text-[#6b7280] uppercase">Для бизнеса</span></div>
            {[{i:Link2,l:'Платёжные ссылки'},{i:Code,l:'API'},{i:Grid3X3,l:'Массовые выплаты'}].map((x,i)=>{const I=x.i;return <a key={i} href="#" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#9ca3af]"><I className="w-5 h-5"/><span>{x.l}</span></a>;})}
            <div className="mt-4 border-t border-[#3d4557] pt-2">
              {[{i:Award,l:'Тарифы'},{i:HelpCircle,l:'Служба поддержки'},{i:GraduationCap,l:'Академия Cashinout'},{i:HelpCircle,l:'Частые вопросы'},{i:LogOut,l:'Выйти'}].map((x,i)=>{const I=x.i;return <a key={i} href="#" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#9ca3af]"><I className="w-5 h-5"/><span>{x.l}</span></a>;})}
            </div>
          </nav>
        </aside>
        <div className="flex-1 flex flex-col min-w-0">
          <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-end gap-4">
            <span className="text-sm text-gray-700 font-medium">{accountName}</span>
            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-medium">{accountName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}</span>
            </div>
            <div className="text-sm text-gray-600">ID: <span className="font-medium">{userId}</span></div>
            <Copy className="w-5 h-5 text-gray-500" />
            <div className="relative"><Bell className="w-5 h-5 text-gray-500" /><span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full"></span></div>
          </header>
          <main className="flex-1 px-5 pt-4 pb-2 overflow-auto">
            <div className="max-w-5xl">
              <h1 className="text-2xl font-semibold text-gray-900 mb-4">История</h1>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input type="text" placeholder="Поиск" className="w-full pl-9 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white text-gray-600"><span>RUB</span><ChevronDown className="w-3 h-3" /></div>
                  <div className="flex items-center gap-1 px-3 py-1.5 border border-blue-400 rounded-lg text-xs bg-blue-50 text-blue-700 font-medium"><span>Исходящие</span><ChevronDown className="w-3 h-3" /></div>
                  <div className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white text-gray-600">
                    <span className="max-w-[180px] truncate">{computedDateFrom && computedDateTo ? `${computedDateFrom} - ${computedDateTo}` : 'Все время'}</span><Calendar className="w-3 h-3 ml-1" />
                  </div>
                  <div className="flex items-center px-2 py-1.5 border border-gray-200 rounded-lg text-xs bg-white text-gray-600"><Download className="w-3.5 h-3.5" /></div>
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left text-[11px] font-medium text-gray-500 px-3 py-1.5">Статус</th>
                      <th className="text-left text-[11px] font-medium text-gray-500 px-3 py-1.5">Время</th>
                      <th className="text-left text-[11px] font-medium text-gray-500 px-3 py-1.5">Валюта</th>
                      <th className="text-left text-[11px] font-medium text-gray-500 px-3 py-1.5">Сумма</th>
                      <th className="text-left text-[11px] font-medium text-gray-500 px-3 py-1.5">Тип</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400 text-xs">Список операций пуст</td></tr>
                    ) : transactions.map((tx, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="px-3 py-[5px]"><div className="flex items-center gap-1"><div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-green-500" /></div><span className="text-[11px] text-green-500">Успешно</span></div></td>
                          <td className="px-3 py-[5px]"><div className="text-[11px] text-gray-900">{tx.date}</div><div className="text-[9px] text-gray-400">{tx.time || '00:00'}</div></td>
                          <td className="px-3 py-[5px]"><span className="text-[11px] text-gray-900">RUB</span></td>
                          <td className="px-3 py-[5px]"><span className="text-[11px] font-medium text-red-500">-{(tx.amount||0).toLocaleString('ru-RU')} &#8381;</span></td>
                          <td className="px-3 py-[5px]"><span className="text-[11px] text-gray-900">Вывод</span></td>
                        </tr>
                    ))}
                  </tbody>
                </table>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-3 py-1.5 border-t border-gray-200">
                    <div className="text-[10px] text-gray-500">Стр. {currentPage} из {totalPages} ({filteredTransactions.length} операций)</div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({length: totalPages}, (_, i) => i+1).map(p => (
                        <button key={p} onClick={() => setCurrentPage(p)} className={`w-6 h-6 rounded text-[11px] font-medium ${currentPage===p ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{p}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
